import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BsPlayFill, BsPauseFill, BsSkipForwardFill } from 'react-icons/bs';
import Confetti from 'react-confetti';
import {
  allExercises as exercises,
  getAllSports,
  getExercisesBySport,
} from '../data/exercises/index.js';
import { sessions } from '../data/sessions';
import { generateSportInstructions, sportConfigs } from '../data/sportConfigs';
import { getExerciseTiming } from '../data/timing-source-of-truth.js';
import { getDrillTimingById } from '../data/skills-drills-timing-source-of-truth.js';
import { getSortedDrills } from '../data/sessions_drills.js';
import { getSkillById } from '../data/new-skills';
import FloatingPlayerIndicator from './FloatingPlayerIndicator';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';
import { getVersionedAudioUrl } from '../utils/audioUrl';

function AudioWorkoutScreen({ addPoints, mode = 'workout' }) {
  const { workoutId, skillId, sport: urlSport } = useParams();
  const navigate = useNavigate();
  const { selectedPlayersData, removePlayer, addPlayer, allPlayersData } =
    useSelectedPlayers();

  // Handle both workout and skill modes
  const isSkillMode = mode === 'skill';

  // Helper function to map old drill IDs to new skill IDs
  const mapDrillIdToSkillId = (drillId) => {
    const drillToSkillMap = {
      // Individual Soccer Drills (Single Skills)
      2011: 1010, // Soccer Basics
      2012: 1007, // Soccer Speed
      2013: 1008, // Soccer Coordination
      2014: 1001, // Soccer Ball Control
      2015: 1009, // Soccer Movement
      2016: 1004, // Soccer Dribbling
      2017: 1011, // Soccer Quick Skills (combined)
      2018: 1005, // Soccer Footwork
      2019: 1006, // Soccer Agility
      2020: 1002, // Soccer Shooting
      2021: 1003, // Soccer Passing
      2022: 1011, // Soccer Quick Skills (combined)

      // Partner Soccer Drills (Single Skills)
      2170: 1012, // Soccer Partner Passing
      2171: 1013, // Soccer Partner Control
      2172: 1014, // Soccer Partner Dribbling
      2173: 1015, // Soccer Partner Coordination
      2174: 1016, // Soccer Partner Movement
      2175: 1017, // Soccer Partner Skills
      2176: 1018, // Soccer Partner Flow
      2177: 1019, // Soccer Partner Rhythm
      2178: 1020, // Soccer Partner Quick Skills
      2179: 1021, // Soccer Partner Advanced
      2180: 1022, // Soccer Partner Master
      2181: 1023, // Soccer Partner Master

      // Karate Drills
      2031: 1024, // Karate Kicking
      2032: 1025, // Karate Punching
      2033: 1026, // Karate Blocking
      2034: 1027, // Karate Stances
      2035: 1028, // Karate Moves
      2036: 1029, // Karate Coordination
      2037: 1030, // Karate Balance
      2038: 1031, // Karate Speed
      2039: 1032, // Karate Power
      2040: 1033, // Advanced Karate

      // Basketball Drills
      2001: 1034, // Basketball Basics
      2002: 1035, // Ball Control
      2003: 1036, // Dribbling Skills
      2004: 1037, // Shooting Basics
      2005: 1038, // Advanced Dribbling
      2006: 1039, // Shooting Skills
      2007: 1040, // Ball Handling
      2008: 1041, // Advanced Skills
      2009: 1042, // Basketball Master
      2010: 1043, // Elite Basketball

      // Baseball Drills
      2140: 1044, // Baseball Basics
      2141: 1045, // Throwing Skills
      2142: 1046, // Catching Skills
      2143: 1047, // Fielding Basics
      2144: 1048, // Advanced Fielding
      2146: 1049, // Throwing Basics
      2147: 1050, // Advanced Throwing
      2148: 1051, // Catching Master
      2149: 1052, // Fielding Master

      // Baseball Partner Drills
      2160: 1053, // Partner Throwing
      2161: 1054, // Partner Catching
      2162: 1055, // Partner Fielding
      2163: 1056, // Advanced Partner
      2164: 1057, // Partner Master
      2165: 1058, // Elite Partner

      // Volleyball Drills
      2051: 1059, // Volleyball Basics
      2052: 1060, // Serving Skills
      2053: 1061, // Passing Skills
      2054: 1062, // Setting Skills
      2055: 1063, // Advanced Serving
      2056: 1064, // Advanced Passing
      2057: 1065, // Advanced Setting
      2058: 1066, // Volleyball Master
      2059: 1067, // Elite Volleyball
      2060: 1068, // Volleyball Champion

      // Volleyball Partner Drills
      2080: 1069, // Partner Passing
      2081: 1070, // Partner Serving
      2082: 1071, // Partner Setting
      2083: 1072, // Advanced Partner
      2084: 1073, // Partner Master
      2085: 1074, // Elite Partner
      2086: 1075, // Partner Champion
      2087: 1076, // Partner Elite
      2088: 1077, // Partner Master
      2089: 1078, // Partner Champion

      // Dance Drills
      2041: 1079, // Dance Basics
      2042: 1080, // Dance Moves
      2043: 1081, // Dance Flow
      2044: 1082, // Dance Rhythm
      2045: 1083, // Advanced Dance
      2046: 1084, // Dance Master
      2047: 1085, // Elite Dance
      2048: 1086, // Dance Champion
      2049: 1087, // Quick Dance
      2050: 1088, // Dance Express

      // Ballet Drills
      2190: 1089, // Ballet Positions
      2191: 1090, // Ballet Moves
      2192: 1091, // Ballet Coordination
      2193: 1092, // Ballet Basics
      2194: 1093, // Ballet Balance
      2195: 1094, // Ballet Grace
      2196: 1095, // Ballet Flow
      2197: 1096, // Ballet Control
      2198: 1097, // Ballet Expression
      2199: 1098, // Advanced Ballet

      // Boxing Drills
      2090: 1099, // Boxing Basics
      2091: 1100, // Power Puncher
      2092: 1101, // Speed Boxer
      2093: 1102, // Defense Master
      2094: 1103, // Boxing Feet
      2095: 1104, // Punch Combos
      2096: 1105, // Agile Boxer
      2097: 1106, // Boxing Master
      2098: 1107, // Elite Boxer
      2099: 1108, // Boxing Champion

      // Yoga Drills
      2100: 1109, // Yoga Flow
      2101: 1110, // Yoga Balance
      2102: 1111, // Yoga Strength

      // Taekwondo Drills
      2120: 1112, // Taekwondo Basics
      2121: 1113, // Taekwondo Kicking
      2122: 1114, // Taekwondo Punching
      2123: 1115, // Taekwondo Blocking
      2124: 1116, // Taekwondo Stances
      2125: 1117, // Advanced Taekwondo
      2126: 1118, // Taekwondo Power
      2127: 1119, // Taekwondo Speed
      2128: 1120, // Taekwondo Control
      2129: 1121, // Taekwondo Master
      2130: 1122, // Elite Taekwondo

      // Movement Coordination Drills
      2061: 1116, // Movement Basics
      2062: 1117, // Coordination Skills
      2063: 1118, // Balance Training
      2064: 1119, // Movement Flow
      2065: 1120, // Advanced Movement
      2066: 1121, // Coordination Master
      2067: 1122, // Balance Master
      2068: 1123, // Movement Master
      2069: 1124, // Elite Movement

      // Martial Arts Drills
      2021: 1125, // Martial Arts Basics
      2022: 1126, // Martial Arts Skills
      2023: 1127, // Martial Arts Flow
      2024: 1128, // Martial Arts Control
      2025: 1129, // Advanced Martial Arts
      2026: 1130, // Martial Arts Master
      2027: 1131, // Elite Martial Arts
      2028: 1132, // Martial Arts Champion
      2029: 1133, // Martial Arts Elite
      2030: 1134, // Martial Arts Master
    };
    return drillToSkillMap[drillId] || drillId;
  };

  // Get the actual skill ID (handle old drill ID mapping)
  const actualSkillId = isSkillMode
    ? mapDrillIdToSkillId(parseInt(skillId))
    : null;

  // Get session data
  const session = isSkillMode
    ? null
    : sessions.find((s) => s.workoutId === parseInt(workoutId));

  // Skill data handling - using new skills structure
  const currentSkill = isSkillMode ? getSkillById(actualSkillId) : null;

  // Helper function to get drill title by ID
  const getDrillTitle = (drillId) => {
    // Map of drill IDs to their titles (we can expand this as needed)
    const drillTitles = {
      // Individual Soccer Drills
      2011: 'Beginner Individual Dribbling',
      2012: 'Intermediate Speed Control',
      2013: 'Intermediate Coordination',
      2014: 'Advanced Ball Control',
      2015: 'Intermediate Movement',
      2016: 'Advanced Dribbling',
      2017: 'Beginner Quick Skills',
      2018: 'Intermediate Footwork',
      2019: 'Intermediate Agility',
      2020: 'Advanced Shooting',
      2021: 'Advanced Passing',
      2022: 'Beginner Quick Skills',

      // Partner Soccer Drills
      2170: 'Partner Passing',
      2171: 'Partner Control',
      2172: 'Partner Dribbling',
      2173: 'Beginner Partner Ball Control',
      2174: 'Partner Movement',
      2175: 'Partner Skills',
      2176: 'Partner Flow',
      2177: 'Partner Rhythm',
      2178: 'Partner Quick Skills',
      2179: 'Partner Advanced',
      2180: 'Partner Master',
      2181: 'Partner Master',

      // Karate Drills
      2031: 'Karate Kicking',
      2032: 'Karate Punching',
      2033: 'Karate Blocking',
      2034: 'Karate Stances',
      2035: 'Karate Moves',
      2036: 'Karate Coordination',
      2037: 'Karate Balance',
      2038: 'Karate Speed',
      2039: 'Karate Power',
      2040: 'Advanced Karate',

      // Basketball Drills
      2001: 'Basketball Basics',
      2002: 'Ball Control',
      2003: 'Dribbling Skills',
      2004: 'Shooting Basics',
      2005: 'Advanced Dribbling',
      2006: 'Shooting Skills',
      2007: 'Ball Handling',
      2008: 'Advanced Skills',
      2009: 'Basketball Master',
      2010: 'Elite Basketball',

      // Baseball Drills
      2140: 'Baseball Basics',
      2141: 'Throwing Skills',
      2142: 'Catching Skills',
      2143: 'Fielding Basics',
      2144: 'Advanced Fielding',
      2146: 'Throwing Basics',
      2147: 'Advanced Throwing',
      2148: 'Catching Master',
      2149: 'Fielding Master',

      // Baseball Partner Drills
      2160: 'Partner Throwing',
      2161: 'Partner Catching',
      2162: 'Partner Fielding',
      2163: 'Advanced Partner',
      2164: 'Partner Master',
      2165: 'Elite Partner',

      // Volleyball Drills
      2051: 'Volleyball Basics',
      2052: 'Serving Skills',
      2053: 'Passing Skills',
      2054: 'Setting Skills',
      2055: 'Advanced Serving',
      2056: 'Advanced Passing',
      2057: 'Advanced Setting',
      2058: 'Volleyball Master',
      2059: 'Elite Volleyball',
      2060: 'Volleyball Champion',

      // Volleyball Partner Drills
      2080: 'Partner Passing',
      2081: 'Partner Serving',
      2082: 'Partner Setting',
      2083: 'Advanced Partner',
      2084: 'Partner Master',
      2085: 'Elite Partner',
      2086: 'Partner Champion',
      2087: 'Partner Elite',
      2088: 'Partner Master',
      2089: 'Partner Champion',

      // Dance Drills
      2041: 'Dance Basics',
      2042: 'Dance Moves',
      2043: 'Dance Flow',
      2044: 'Dance Rhythm',
      2045: 'Advanced Dance',
      2046: 'Dance Master',
      2047: 'Elite Dance',
      2048: 'Dance Champion',
      2049: 'Quick Dance',
      2050: 'Dance Express',

      // Ballet Drills
      2190: 'Ballet Positions',
      2191: 'Ballet Moves',
      2192: 'Ballet Coordination',
      2193: 'Ballet Basics',
      2194: 'Ballet Balance',
      2195: 'Ballet Grace',
      2196: 'Ballet Flow',
      2197: 'Ballet Control',
      2198: 'Ballet Expression',
      2199: 'Advanced Ballet',

      // Boxing Drills
      2090: 'Boxing Basics',
      2091: 'Power Puncher',
      2092: 'Speed Boxer',
      2093: 'Defense Master',
      2094: 'Boxing Feet',
      2095: 'Punch Combos',
      2096: 'Agile Boxer',
      2097: 'Boxing Master',
      2098: 'Elite Boxer',
      2099: 'Boxing Champion',

      // Yoga Drills
      2100: 'Yoga Flow',
      2101: 'Yoga Balance',
      2102: 'Yoga Strength',

      // Taekwondo Drills
      2120: 'Taekwondo Basics',
      2121: 'Taekwondo Kicking',
      2122: 'Taekwondo Punching',
      2123: 'Taekwondo Blocking',
      2124: 'Taekwondo Stances',
      2125: 'Advanced Taekwondo',
      2126: 'Taekwondo Power',
      2127: 'Taekwondo Speed',
      2128: 'Taekwondo Control',
      2129: 'Taekwondo Master',
      2130: 'Elite Taekwondo',

      // Movement Coordination Drills
      2061: 'Movement Basics',
      2062: 'Coordination Skills',
      2063: 'Balance Training',
      2064: 'Movement Flow',
      2065: 'Advanced Movement',
      2066: 'Coordination Master',
      2067: 'Balance Master',
      2068: 'Movement Master',
      2069: 'Elite Movement',

      // Martial Arts Drills
      2021: 'Martial Arts Basics',
      2022: 'Martial Arts Skills',
      2023: 'Martial Arts Flow',
      2024: 'Martial Arts Control',
      2025: 'Advanced Martial Arts',
      2026: 'Martial Arts Master',
      2027: 'Elite Martial Arts',
      2028: 'Martial Arts Champion',
      2029: 'Martial Arts Elite',
      2030: 'Martial Arts Master',
    };
    return drillTitles[drillId] || `Drill ${drillId}`;
  };

  // Convert skill drills to the format expected by AudioWorkoutScreen
  const skillDrills =
    isSkillMode && currentSkill
      ? currentSkill.drills.map((drillId) => {
          // Get drill timing data for audio path detection
          const drillTiming = getDrillTimingById(drillId);
          // Check if this is a partner drill based on skill emoji
          const isPartnerDrill = currentSkill.emoji === '👥';

          // Calculate duration and points per drill (divide skill total by number of drills)
          const drillsCount = currentSkill.drills.length;
          const skillDuration = parseInt(currentSkill.duration) || 0;
          const skillPoints = currentSkill.points || 0;

          return {
            drillId: drillId,
            title: getDrillTitle(drillId), // Use actual drill title
            duration: Math.round(skillDuration / drillsCount), // Use skill duration divided by drill count
            points: Math.round(skillPoints / drillsCount), // Use skill points divided by drill count
            description: getDrillTitle(drillId),
            requiresPartner: isPartnerDrill, // Set this for audio path detection
          };
        })
      : [];

  // Skill information
  const skillInfo = {
    101: {
      name: 'Dribbling Master',
      icon: '⚽',
      description: 'Learn to control the ball with your feet',
      intro:
        "Hey soccer stars! Ready to become a dribbling master? This skill will teach you to control the ball like a pro! You'll need a soccer ball and some space to move around. Let's get started!",
      completion:
        "AMAZING! You're a soccer DRIBBLING MASTER! You just learned to control the ball with your feet like a professional! Your ball control is incredible now, and you're going to weave through defenders like magic! You're ready to be the best dribbler on your team! You're a true champion!",
    },
    102: {
      name: 'Passing Pro',
      icon: '🔄',
      description: 'Master accurate passing to teammates',
      intro:
        "Hey soccer stars! Ready to become a passing pro? This skill will teach you to pass the ball accurately to your teammates! You'll need a soccer ball and a wall or a partner to practice with. Let's get started!",
      completion:
        "INCREDIBLE! You're a soccer PASSING PRO! You just learned to pass the ball with perfect accuracy! Your passing skills are amazing now, and you're going to set up your teammates for goals! You're ready to be the best passer on your team! You're a true champion!",
    },
    103: {
      name: 'Technique Practice',
      icon: '🎯',
      description: 'Master soccer technique and form',
      intro:
        "Hey soccer stars! Ready to master soccer technique? This skill will teach you proper form and ball control! You'll need a soccer ball and some space to practice. Let's get started!",
      completion:
        "FANTASTIC! You're a soccer TECHNIQUE MASTER! You just learned proper soccer form and ball control! Your technique is incredible now, and you're going to play like a professional! You're ready to be the most skilled player on your team! You're a true champion!",
    },
    104: {
      name: 'Partner Passing',
      icon: '👥',
      description: 'Master teamwork through passing',
      intro:
        "Hey soccer stars! Ready to become partner passing pros? This skill will teach you to work together as a team! You'll need a soccer ball and a partner to practice with. Let's get started!",
      completion:
        "AMAZING! You're soccer PARTNER PASSING PROS! You just learned to pass the ball perfectly with your partner! Your teamwork is incredible now, and you're going to dominate the field together! You're ready to be the best passing duo on your team! You're true champions!",
    },
    105: {
      name: 'Partner Control',
      icon: '🎯',
      description: 'Control the ball with your partner',
      intro:
        "Hey soccer stars! Ready to master partner ball control? This skill will teach you to control the ball together! You'll need a soccer ball and a partner to practice with. Let's get started!",
      completion:
        "INCREDIBLE! You're soccer PARTNER CONTROL MASTERS! You just learned to control the ball perfectly with your partner! Your coordination is amazing now, and you're going to work together like magic! You're ready to be the best control duo on your team! You're true champions!",
    },
    106: {
      name: 'Partner Dribbling',
      icon: '🏃',
      description: 'Dribble together as a team',
      intro:
        "Hey soccer stars! Ready to become partner dribbling stars? This skill will teach you to dribble together as a team! You'll need a soccer ball and a partner to practice with. Let's get started!",
      completion:
        "FANTASTIC! You're soccer PARTNER DRIBBLING STARS! You just learned to dribble together with perfect coordination! Your teamwork is incredible now, and you're going to move the ball like professionals! You're ready to be the best dribbling duo on your team! You're true champions!",
    },
    107: {
      name: 'Partner Skill Moves',
      icon: '🌟',
      description: 'Advanced partner coordination drills',
      intro:
        "Hey soccer stars! Ready to master advanced partner skills? This skill will teach you complex coordination moves! You'll need a soccer ball and a partner to practice with. Let's get started!",
      completion:
        "AMAZING! You're soccer PARTNER SKILLS MASTERS! You just learned advanced coordination moves with your partner! Your teamwork is incredible now, and you're going to perform like professionals! You're ready to be the most skilled duo on your team! You're true champions!",
    },
  };

  const skillDisplayData = isSkillMode
    ? currentSkill
      ? {
          name: currentSkill.title,
          icon: currentSkill.emoji || '👤', // Use emoji from skill data, fallback to individual
          description: currentSkill.equipment || currentSkill.description, // Use equipment if available, fallback to description
          intro: `Welcome to ${currentSkill.title}!`,
          completion: `Great job completing ${currentSkill.title}!`,
        }
      : {
          name: 'Skill',
          icon: '🎯',
          description: 'Skill session',
          intro: 'Welcome!',
          completion: 'Great job!',
        }
    : session
    ? {
        name: session.title,
        icon: '🏆',
        description: session.description || 'Workout session',
        intro: `Welcome to ${session.title}!`,
        completion: `Great job completing ${session.title}!`,
      }
    : null;

  // Derive sport from session data by checking the exercises
  const deriveSportFromSession = (session) => {
    if (!session) return null;

    // Get all exercises for all sports
    const allSports = getAllSports();

    // Check each sport to see if it has exercises that match this session
    for (const sportName of allSports) {
      const sportExercises = getExercisesBySport(sportName);

      // Check if any exercise in the session belongs to this sport
      const hasMatchingExercise = session.exercises.some((exerciseId) => {
        const exercise = sportExercises.find((ex) => ex.id === exerciseId);
        return exercise && exercise.sports.includes(sportName);
      });

      if (hasMatchingExercise) {
        // For now, prioritize soccer for Lightning Fast Feet workout
        if (session.workoutId === 1 && sportName === 'soccer') {
          return sportName;
        }
        // For other workouts, return the first match
        return sportName;
      }
    }

    return null;
  };

  // Use sport from URL if available, otherwise derive from session
  const sport =
    urlSport || (isSkillMode ? null : deriveSportFromSession(session));

  const [currentExercise, setCurrentExercise] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [showIntroScreen, setShowIntroScreen] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentInstructionIndex, setCurrentInstructionIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [totalExerciseTime, setTotalExerciseTime] = useState(0);
  const [isExerciseActive, setIsExerciseActive] = useState(false);
  const legLeftRef = useRef(null);
  const legRightRef = useRef(null);
  const timerRef = useRef(null);
  const currentInstructionIndexRef = useRef(0);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const audioRef = useRef(null);
  const currentExerciseRef = useRef(0); // Add ref to track current exercise
  const isProcessingNextRef = useRef(false); // Add ref to prevent rapid multiple calls

  // Get current exercise/drill using the ID from session.exercises array or skillDrills array
  const exercise = isSkillMode
    ? skillDrills[currentExercise]
    : currentExerciseData;

  // Note: currentExerciseData is set by useEffect when currentExercise changes

  // Audio file paths

  const getAudioPath = (type, exerciseName = null, exerciseIndex = null) => {
    let path = null;

    if (isSkillMode) {
      // Drill-based skill audio paths
      const targetIndex =
        exerciseIndex !== null ? exerciseIndex : currentExercise;
      const selected = skillDrills[targetIndex] || skillDrills[0];
      if (!selected) return null;
      const drillId = selected.drillId;
      const isPartnerDrill = !!selected.requiresPartner;
      const regularMap = {
        soccer: 'latest-generated-drills-audio',
        basketball: 'latest-generated-basketball-drills-audio',
        baseball: 'latest-generated-baseball-drills-audio',
        karate: 'latest-generated-karate-drills-audio',
        taekwondo: 'latest-generated-taekwondo-drills-audio',
        volleyball: 'latest-generated-volleyball-drills-audio',
        gymnastics: 'latest-generated-movement-coordination-drills-audio',
        'martial-arts': 'latest-generated-martial-arts-drills-audio',
        martial_arts: 'latest-generated-martial-arts-drills-audio',
        dance: 'latest-generated-dance-drills-audio',
        ballet: 'latest-generated-ballet-drills-audio',
        boxing: 'latest-generated-boxing-drills-audio',
        yoga: 'latest-generated-yoga-drills-audio',
        'running-technique': 'latest-generated-running-technique-drills-audio',
      };
      const audioFolder = isPartnerDrill
        ? 'latest-generated-partner-drills-audio'
        : regularMap[sport] || 'latest-generated-drills-audio';

      if (type === 'intro') {
        // Use the drill intro as the session intro
        path = `/${audioFolder}/${sport}_${
          isPartnerDrill ? 'partner_drill' : 'drill'
        }_${drillId}_intro.mp3`;
      } else if (type === 'conclusion') {
        // No explicit conclusion per drill; return null
        path = null;
      } else if (type === 'exercise') {
        // Main instructions
        path = `/${audioFolder}/${sport}_${
          isPartnerDrill ? 'partner_drill' : 'drill'
        }_${drillId}_instructions.mp3`;
      } else if (type === 'exerciseIntro') {
        // Avoid double-intro; return null so we go straight to main audio
        path = null;
      }
    } else {
      // Workout audio paths
      if (type === 'intro') {
        // Workout intro: /latest-generated-workout-audio/soccer_session_1_intro.mp3
        const sportName = sport.replace(/-/g, '_'); // Convert hyphens to underscores
        path = `/latest-generated-workout-audio/${sportName}_session_${workoutId}_intro.mp3`;
      } else if (type === 'conclusion') {
        // Workout conclusion: /latest-generated-workout-audio/soccer_session_1_conclusion.mp3
        const sportName = sport.replace(/-/g, '_'); // Convert hyphens to underscores
        path = `/latest-generated-workout-audio/${sportName}_session_${workoutId}_conclusion.mp3`;
      } else if (type === 'exercise' && exerciseName) {
        // Exercise audio: /latest-generated-exercises-audio/fastFeet.mp3
        const exerciseFileName = convertExerciseNameToFileName(exerciseName);
        path = `/latest-generated-exercises-audio/${exerciseFileName}.mp3`;
      } else if (type === 'exerciseIntro' && exerciseName) {
        // Exercise intro audio: /latest-generated-exercises-audio/fastFeet_intro.mp3
        const exerciseFileName = convertExerciseNameToFileName(exerciseName);
        path = `/latest-generated-exercises-audio/${exerciseFileName}_intro.mp3`;
      }
    }

    return path;
  };

  // Convert exercise name to audio file name
  const convertExerciseNameToFileName = (exerciseName) => {
    // Map exercise names to their audio file names
    const exerciseNameMap = {
      'Fast Feet': 'fastFeet',
      'High Knees': 'highKnees',
      'Mountain Climbers': 'mountainClimbers',
      'Star Jumps': 'starJumps',
      'Skater Hops': 'skaterHops',
      'One Leg Stand': 'oneLegStand',
      'Side Lunges': 'sideLunges',
      'Big Arm Swings': 'bigArmSwings',
      'Rainbow Reaches': 'rainbowReaches',
      'Frog Jumps': 'frogJumps',
      'Mini Squats': 'miniSquats',
      Burpees: 'burpees',
      'Tiger Pounces': 'tigerPounces',
      'Bear Crawl': 'bearCrawl',
      'Crab Walk': 'crabWalk',
      'Zebra Gallops': 'zebraGallops',
      'Rabbit Hops': 'rabbitHops',
      Plank: 'plank',
      'Push-ups': 'pushUps',
      'Superman Holds': 'supermanHolds',
      'Wall Sits': 'wallSits',
      'Bridge Holds': 'bridgeHolds',
      'Upper Cuts': 'upperCuts',
      'Shoulder Taps': 'shoulderTaps',
      'Tricep Dips': 'tricepDips',
      'Inchworm Walk': 'inchwormWalk',
      'Arm Circles': 'armCircles',
      'Jumping Jacks': 'jumpingJacks',
      'Tuck Jumps': 'tuckJumps',
      'Split Jumps': 'splitJumps',
      'Step Jumps': 'stepJumps',
      'Diamond Push-ups': 'diamondPushUps',
      'Pike Push-ups': 'pikePushUps',
      'Dragon Walk': 'dragonWalk',
      Thrusters: 'thrusters',
      'Sprint Intervals': 'sprintIntervals',
    };

    return (
      exerciseNameMap[exerciseName] ||
      exerciseName.toLowerCase().replace(/\s+/g, '')
    );
  };

  // Play audio file
  const playAudio = (audioPath, onEnd) => {
    if (!audioPath) {
      setAudioError({
        type: 'missing_path',
        path: 'No audio path provided',
        message: 'Audio file path is missing',
      });
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Add timestamp versioning for automatic cache invalidation
    const versionedAudioPath = getVersionedAudioUrl(audioPath);
    audioRef.current = new Audio(versionedAudioPath);
    audioRef.current.onended = () => {
      setIsAudioPlaying(false);
      setAudioError(null); // Clear any previous errors
      if (onEnd) onEnd();
    };
    audioRef.current.onerror = (error) => {
      console.error('Audio playback error:', error);
      setIsAudioPlaying(false);
      setAudioError({
        type: 'playback',
        path: audioPath,
        message: 'Audio file could not be loaded or played',
      });
    };
    audioRef.current
      .play()
      .then(() => {
        setIsAudioPlaying(true);
        setAudioError(null); // Clear any previous errors
      })
      .catch((error) => {
        console.error('Audio play error:', error);
        setIsAudioPlaying(false);
        setAudioError({
          type: 'playback',
          path: audioPath,
          message: 'Audio file could not be loaded or played',
        });
      });
  };

  // Stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsAudioPlaying(false);

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle error reporting
  const handleReportError = () => {
    const fullRoute = window.location.pathname;
    const subject = encodeURIComponent('Error detected');
    const body =
      encodeURIComponent(`There's an error in missing Audio for ${fullRoute}

Audio Path: ${audioError?.path || 'Unknown'}
Error Type: ${audioError?.type || 'Unknown'}
Error Message: ${audioError?.message || 'Unknown'}

Please investigate this audio playback issue.`);

    const mailtoLink = `mailto:info@kiddifit.com?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
  };

  // Clear error
  const clearError = () => {
    setAudioError(null);
  };

  const startTimedInstructions = (exerciseIndex = null) => {
    // Use provided exerciseIndex or fall back to currentExercise ref
    const targetExerciseIndex =
      exerciseIndex !== null ? exerciseIndex : currentExerciseRef.current;

    let targetExerciseData;

    if (isSkillMode) {
      // Get drill data for skill mode
      targetExerciseData = skillDrills[targetExerciseIndex];

      if (!targetExerciseData) {
        console.error('❌ No drill data found for index:', targetExerciseIndex);
        return;
      }
    } else {
      // Get exercise data for workout mode
      const targetExerciseId = session.exercises[targetExerciseIndex];
      targetExerciseData = exercises.find((ex) => ex.id === targetExerciseId);

      if (!targetExerciseData) {
        console.error(
          '❌ No exercise data found for index:',
          targetExerciseIndex
        );
        return;
      }
    }

    // Set the current exercise data for display
    setCurrentExerciseData(targetExerciseData);

    // Handle timing based on mode
    if (isSkillMode) {
      // For drills-as-skills, use the declared drill duration to keep UI consistent
      const drillDuration = targetExerciseData.duration * 60;
      setTotalExerciseTime(drillDuration);
    } else {
      // For workouts, use the new source of truth for accurate timing
      // Convert exercise name to match source of truth format
      const convertExerciseNameToKey = (name) => {
        const nameMap = {
          'Fast Feet': 'fastFeet',
          'High Knees': 'highKnees',
          'Mountain Climbers': 'mountainClimbers',
          'Star Jumps': 'starJumps',
          'Skater Hops': 'skaterHops',
          'One Leg Stand': 'oneLegStand',
          'Side Lunges': 'sideLunges',
          'Big Arm Swings': 'bigArmSwings',
          'Rainbow Reaches': 'rainbowReaches',
          'Frog Jumps': 'frogJumps',
          'Mini Squats': 'miniSquats',
          Burpees: 'burpees',
          'Tiger Pounces': 'tigerPounces',
          'Bear Crawl': 'bearCrawl',
          'Crab Walk': 'crabWalk',
          'Zebra Gallops': 'zebraGallops',
          'Rabbit Hops': 'rabbitHops',
          Plank: 'plank',
          'Push-ups': 'pushUps',
          'Superman Holds': 'supermanHolds',
          'Wall Sits': 'wallSits',
          'Bridge Holds': 'bridgeHolds',
          'Upper Cuts': 'upperCuts',
          'Shoulder Taps': 'shoulderTaps',
          'Tricep Dips': 'tricepDips',
          'Inchworm Walk': 'inchwormWalk',
          'Arm Circles': 'armCircles',
          'Jumping Jacks': 'jumpingJacks',
          // Advanced exercises
          'Mountain Climber Burpees': 'mountainclimberburpees',
          'Jump Squats': 'jumpsquats',
          'Tuck Jumps': 'tuckjumps',
          'Split Jumps': 'splitjumps',
          'Step Jumps': 'stepjumps',
          'Sprint Intervals': 'sprintintervals',
          'Diamond Push-ups': 'diamondpush-ups',
          'Pike Push-ups': 'pikepush-ups',
          'Single Leg Squats': 'single-legsquats',
          'L-Sit Hold': 'l-sithold',
          'Dragon Walk': 'dragonwalk',
          'Single Leg Deadlifts': 'single-legdeadlifts',
          Thrusters: 'thrusters',
        };
        return nameMap[name] || name;
      };

      const exerciseKey = convertExerciseNameToKey(targetExerciseData.name);
      const exerciseTiming = getExerciseTiming(exerciseKey);

      setTotalExerciseTime(exerciseTiming.combinedTime);
    }

    setCurrentTime(0);

    // Play exercise intro first, then main exercise audio
    const exerciseName = isSkillMode
      ? targetExerciseData.title
      : targetExerciseData.name;

    const exerciseIntroPath = getAudioPath(
      'exerciseIntro',
      exerciseName,
      targetExerciseIndex
    );

    if (exerciseIntroPath) {
      // Start timer when intro begins
      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setCurrentTime(elapsed);
      }, 1000);

      // Play intro first
      playAudio(exerciseIntroPath, () => {
        // When intro ends, play the main exercise audio
        console.log(
          '✅ Intro finished, starting main ' +
            (isSkillMode ? 'drill' : 'exercise') +
            ' audio'
        );

        const exerciseAudioPath = getAudioPath(
          'exercise',
          exerciseName,
          targetExerciseIndex
        );
        console.log(
          '🎵 Main ' + (isSkillMode ? 'drill' : 'exercise') + ' audio path:',
          exerciseAudioPath
        );

        if (exerciseAudioPath) {
          playAudio(exerciseAudioPath, () => {
            // When main exercise audio ends, move to next exercise
            console.log(
              '✅ Main ' +
                (isSkillMode ? 'drill' : 'exercise') +
                ' audio finished for:',
              exerciseName
            );
            handleNext();
          });
        } else {
          // No exercise audio found, show error
          console.log(
            '⚠️ No main ' +
              (isSkillMode ? 'drill' : 'exercise') +
              ' audio file found'
          );
          setAudioError({
            type: 'missing_exercise',
            path: 'Exercise audio not found',
            message: `The ${
              isSkillMode ? 'drill' : 'exercise'
            } audio file is missing`,
          });
        }
      });
    } else {
      // No intro file, go straight to main exercise audio
      console.log(
        '⚠️ No intro file found, playing main ' +
          (isSkillMode ? 'drill' : 'exercise') +
          ' audio directly'
      );

      const exerciseAudioPath = getAudioPath(
        'exercise',
        exerciseName,
        targetExerciseIndex
      );
      console.log(
        '🎵 Main ' + (isSkillMode ? 'drill' : 'exercise') + ' audio path:',
        exerciseAudioPath
      );

      if (exerciseAudioPath) {
        playAudio(exerciseAudioPath, () => {
          // When audio ends, move to next exercise
          console.log('✅ Audio finished for:', exerciseName);
          handleNext();
        });
      } else {
        // No exercise audio found, show error
        console.log('⚠️ No audio file found');
        setAudioError({
          type: 'missing_exercise',
          path: 'Exercise audio not found',
          message: `The ${
            isSkillMode ? 'drill' : 'exercise'
          } audio file is missing`,
        });
      }

      // Start timer
      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setCurrentTime(elapsed);
      }, 1000);
    }
  };

  const handleNext = () => {
    // Prevent rapid multiple calls
    if (isProcessingNextRef.current) {
      return;
    }

    isProcessingNextRef.current = true;

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const nextExercise = currentExerciseRef.current + 1;
    const totalItems = isSkillMode
      ? skillDrills.length
      : session.exercises.length;

    if (nextExercise < totalItems) {
      // Update both state and ref
      setCurrentExercise(nextExercise);
      currentExerciseRef.current = nextExercise;
      setCurrentInstructionIndex(0);
      currentInstructionIndexRef.current = 0;
      setCurrentTime(0);
      setIsPaused(false);

      // Start the next exercise after a short delay to ensure state is updated
      setTimeout(() => {
        startTimedInstructions(nextExercise);
        isProcessingNextRef.current = false; // Reset flag after starting next exercise
      }, 200);
    } else {
      // Workout/Skill complete
      setShowCompletionScreen(true);
      stopAudio();

      // Play conclusion audio
      const conclusionAudioPath = getAudioPath('conclusion');

      // Check if conclusion audio file exists - if not, skip conclusion
      const isNewWorkout =
        parseInt(workoutId) >= 17 && parseInt(workoutId) <= 28;

      // Check if conclusion audio file exists - if not, skip conclusion
      const sportsWithWorkoutAudio = [
        'baseball',
        'basketball',
        'cricket',
        'dance',
        'football',
        'golf',
        'gymnastics',
        'hockey',
        'lacrosse',
        'martial-arts',
        'rugby',
        'soccer',
        'swimming',
        'tennis',
        'track',
        'volleyball',
      ];
      const hasConclusionAudio =
        conclusionAudioPath &&
        !isNewWorkout &&
        sportsWithWorkoutAudio.includes(sport);

      if (hasConclusionAudio) {
        playAudio(conclusionAudioPath);
      }

      // Add points for completing the session
      if (isSkillMode) {
        const totalPoints = skillDrills.reduce(
          (sum, drill) => sum + drill.points,
          0
        );
        addPoints('skill', {
          skillId: skillId,
          points: totalPoints,
          workoutData: {
            skillId: skillId,
            skillName: skillDisplayData.name,
            skillEmoji: skillDisplayData.icon, // Add emoji for badge/achievement use
            drills: skillDrills,
          },
        });
      } else {
        addPoints('session', {
          workoutId: session.workoutId,
          points: session.completion.points,
          workoutData: session,
        });
        // Ensure floating indicator stays visible on completion
        setTimeout(() => {
          const indicator = document.querySelector(
            '.floating-player-indicator'
          );
          if (indicator) indicator.style.display = 'block';
        }, 0);
      }
    }

    // Reset flag at the end
    isProcessingNextRef.current = false;
  };

  useEffect(() => {
    if (!isSkillMode && !session) {
      navigate('/');
      return;
    }

    const handleBeforeUnload = () => {
      stopAudio();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      stopAudio();
    };
  }, [session, navigate, isSkillMode]);

  const handleStartExercise = () => {
    setIsExerciseActive(true);
    setShowIntroScreen(false);
    startTimedInstructions();
  };

  const startWorkoutFlow = () => {
    setShowStartScreen(false);
    setShowIntroScreen(true);

    // For skills, skip intro audio and go straight to drill instructions
    if (isSkillMode) {
      console.log(
        '🎯 Skill mode - skipping intro audio, going straight to drill instructions'
      );
      setShowIntroScreen(false);
      setShowStartScreen(false);
      startTimedInstructions();
      return;
    }

    // Play intro audio for workouts
    const introAudioPath = getAudioPath('intro');
    console.log('🎵 Intro audio path:', introAudioPath);

    // Check if intro audio file exists - if not, skip intro and go straight to exercises
    const isNewWorkout = parseInt(workoutId) >= 17 && parseInt(workoutId) <= 28;

    // Sports that have workout audio files
    const sportsWithWorkoutAudio = [
      'baseball',
      'basketball',
      'cricket',
      'dance',
      'football',
      'golf',
      'gymnastics',
      'hockey',
      'lacrosse',
      'martial-arts',
      'rugby',
      'soccer',
      'swimming',
      'tennis',
      'track',
      'volleyball',
    ];

    const hasIntroAudio =
      introAudioPath && !isNewWorkout && sportsWithWorkoutAudio.includes(sport);

    if (hasIntroAudio) {
      playAudio(introAudioPath, () => {
        // When intro audio ends, start the first exercise
        setShowIntroScreen(false);
        setShowStartScreen(false);
        startTimedInstructions();
      });
    } else {
      // No intro audio found - skip intro and go straight to exercises
      console.log(
        '⚠️ No intro audio found - skipping intro and going straight to exercises'
      );
      setShowIntroScreen(false);
      setShowStartScreen(false);
      startTimedInstructions();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Verbose time for skills/drills (non-workout): "Xm Ys"
  const formatTimeVerbose = (seconds) => {
    const safe = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${mins}m ${secs}s`;
  };

  const getExerciseName = (exerciseId) => {
    if (isSkillMode) {
      const drill = skillDrills[exerciseId];
      return drill ? drill.title : 'Unknown Drill';
    }
    const exercise = exercises.find((ex) => ex.id === exerciseId);
    return exercise ? exercise.name : 'Unknown Exercise';
  };

  const getExerciseTime = (exerciseId) => {
    if (isSkillMode) {
      const drill = skillDrills[exerciseId];
      return drill ? drill.duration * 60 : 0; // Convert minutes to seconds
    }

    const exercise = exercises.find((ex) => ex.id === exerciseId);
    if (!exercise) return 0;

    // Use the new source of truth for accurate timing
    const convertExerciseNameToKey = (name) => {
      const nameMap = {
        'Fast Feet': 'fastFeet',
        'High Knees': 'highKnees',
        'Mountain Climbers': 'mountainClimbers',
        'Star Jumps': 'starJumps',
        'Skater Hops': 'skaterHops',
        'One Leg Stand': 'oneLegStand',
        'Side Lunges': 'sideLunges',
        'Big Arm Swings': 'bigArmSwings',
        'Rainbow Reaches': 'rainbowReaches',
        'Frog Jumps': 'frogJumps',
        'Mini Squats': 'miniSquats',
        Burpees: 'burpees',
        'Tiger Pounces': 'tigerPounces',
        'Bear Crawl': 'bearCrawl',
        'Crab Walk': 'crabWalk',
        'Zebra Gallops': 'zebraGallops',
        'Rabbit Hops': 'rabbitHops',
        Plank: 'plank',
        'Push-ups': 'pushUps',
        'Superman Holds': 'supermanHolds',
        'Wall Sits': 'wallSits',
        'Bridge Holds': 'bridgeHolds',
        'Upper Cuts': 'upperCuts',
        'Shoulder Taps': 'shoulderTaps',
        'Tricep Dips': 'tricepDips',
        'Inchworm Walk': 'inchwormWalk',
        'Arm Circles': 'armCircles',
        'Jumping Jacks': 'jumpingJacks',
      };
      return nameMap[name] || name;
    };

    const exerciseKey = convertExerciseNameToKey(exercise.name);
    const exerciseTiming = getExerciseTiming(exerciseKey);
    return exerciseTiming.combinedTime;
  };

  const getExercisePoints = (exerciseId) => {
    if (isSkillMode) {
      const drill = skillDrills[exerciseId];
      return drill ? drill.points : 0;
    }
    // Workouts: 1 point per minute based on actual audio timing
    const seconds = getExerciseTime(exerciseId);
    return Math.floor(seconds / 60);
  };

  const handleBackToWorkouts = () => {
    stopAudio();
    if (isSkillMode) {
      navigate(`/skills/sports/${sport}`);
    } else {
      navigate(`/workouts/sports/${sport}`);
    }
  };

  const handleBackToLeaderboard = () => {
    stopAudio();
    navigate(`/leaderboard`);
  };

  const handleEndWorkout = () => {
    const activityType = isSkillMode ? 'Skill' : 'Workout';
    const confirmed = window.confirm(
      `Cancel ${activityType}?\n\nAre you sure you want to end this ${activityType.toLowerCase()}?`
    );
    if (confirmed) {
      stopAudio();
      if (isSkillMode) {
        // Navigate back to the specific sport's skills page
        navigate(`/skills/sports/${urlSport}`);
      } else {
        // Navigate back to the specific sport's workouts page
        navigate(`/workouts/sports/${urlSport}`);
      }
    }
  };

  // Note: currentExerciseData is managed directly in startTimedInstructions

  // Validate data based on mode
  if (!isSkillMode && !session) {
    return <div>Session not found</div>;
  }

  if (isSkillMode && (!skillDrills || skillDrills.length === 0)) {
    console.error('❌ No skill drills found for skill ID:', skillId);
    return <div>No drills found for this skill.</div>;
  }

  if (isSkillMode && !currentSkill) {
    console.error('❌ No skill info found for skill ID:', skillId);
    return <div>Skill not found.</div>;
  }

  const currentExerciseName = isSkillMode
    ? skillDrills[currentExerciseRef.current]?.title
    : getExerciseName(session.exercises[currentExerciseRef.current]);
  const totalExercises = isSkillMode
    ? skillDrills.length
    : session.exercises.length;
  const progress = ((currentExerciseRef.current + 1) / totalExercises) * 100;

  // Add error boundary
  try {
    return (
      <div className='workout-screen'>
        <FloatingPlayerIndicator
          selectedPlayers={selectedPlayersData}
          onRemovePlayer={removePlayer}
          onAddPlayer={addPlayer}
          allPlayers={allPlayersData}
        />
        {showCompletionScreen && <Confetti />}

        {/* Audio Error Display */}
        {audioError && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#000',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              zIndex: 1000,
              maxWidth: '400px',
              textAlign: 'center',
            }}
          >
            <h1
              style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: 'bold',
              }}
            >
              Playback Error
            </h1>
            <p>So sorry! There's something wrong with the audio content.</p>
            <div
              style={{
                display: 'flex',
                marginTop: '1.5rem',
                justifyContent: 'space-between',
              }}
            >
              <button
                onClick={handleReportError}
                style={{
                  background: '#ff6b6b',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#ff5252';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#ff6b6b';
                }}
              >
                Report Error
              </button>
              <button
                onClick={clearError}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#6c757d';
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {showStartScreen && (
          <div>
            <button className='back-button' onClick={handleBackToWorkouts}>
              ← Back to {isSkillMode ? 'Skills' : 'Workouts'}
            </button>
            <div className='start-screen'>
              {isSkillMode && (
                <div
                  style={{
                    fontSize: '48px',
                    textAlign: 'center',
                    marginBottom: '10px',
                  }}
                >
                  {skillDisplayData.icon}
                </div>
              )}
              <h2>{isSkillMode ? skillDisplayData.name : session.title}</h2>
              {skillDisplayData && skillDisplayData.description && (
                <p
                  style={{
                    textAlign: 'center',
                    marginBottom: '20px',
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {skillDisplayData.description}
                </p>
              )}
              <p>
                {isSkillMode ? skillDrills.length : session.exercises.length}{' '}
                {isSkillMode
                  ? skillDrills.length === 1
                    ? 'Drill'
                    : 'Drills'
                  : session.exercises.length === 1
                  ? 'Exercise'
                  : 'Exercises'}{' '}
                •{' '}
                {isSkillMode
                  ? `${skillDrills.reduce(
                      (sum, drill) => sum + drill.duration,
                      0
                    )}m`
                  : formatTime(
                      session.exercises.reduce(
                        (total, exerciseId) =>
                          total + parseInt(getExerciseTime(exerciseId)),
                        0
                      )
                    )}{' '}
                •{' '}
                {isSkillMode
                  ? skillDrills.reduce((sum, drill) => sum + drill.points, 0)
                  : Math.round(
                      session.exercises.reduce(
                        (total, exerciseId) =>
                          total + parseInt(getExerciseTime(exerciseId)),
                        0
                      ) / 60
                    )}{' '}
                Points
              </p>
              <button onClick={startWorkoutFlow} disabled={isAudioPlaying}>
                Start {isSkillMode ? 'Skill' : 'Workout'}
              </button>
            </div>
          </div>
        )}

        {showIntroScreen && (
          <div>
            <button className='back-button' onClick={handleBackToWorkouts}>
              ← Back to {isSkillMode ? 'Skills' : 'Workouts'}
            </button>

            {isSkillMode && (
              <div
                style={{
                  fontSize: '48px',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                {skillDisplayData.icon}
              </div>
            )}
            <h2>{isSkillMode ? skillDisplayData.name : session.title}</h2>
            {skillDisplayData && skillDisplayData.description && (
              <p
                style={{
                  textAlign: 'center',
                  marginBottom: '15px',
                  fontSize: '16px',
                  color: '#666',
                  fontStyle: 'italic',
                  whiteSpace: 'pre-line',
                }}
              >
                {skillDisplayData.description}
              </p>
            )}
            <div className='intro-screen'>
              <div className='exercise-list'>
                <h4>{isSkillMode ? 'Skill Plan' : 'Workout Plan'}</h4>
                <ul style={{ textAlign: 'center' }}>
                  {(isSkillMode ? skillDrills : session.exercises).map(
                    (item, index) => (
                      <li
                        key={index}
                        className={
                          index === currentExerciseRef.current
                            ? 'current-exercise'
                            : ''
                        }
                      >
                        <b>
                          {isSkillMode ? item.title : getExerciseName(item)}
                        </b>{' '}
                        •{' '}
                        {isSkillMode
                          ? `${item.duration}m`
                          : formatTime(getExerciseTime(item))}{' '}
                        • {isSkillMode ? item.points : getExercisePoints(item)}{' '}
                        Points
                      </li>
                    )
                  )}
                </ul>
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  Total Time:{' '}
                  {isSkillMode
                    ? `${skillDrills.reduce(
                        (sum, drill) => sum + drill.duration,
                        0
                      )}m`
                    : formatTime(
                        session.exercises.reduce(
                          (total, exerciseId) =>
                            total + parseInt(getExerciseTime(exerciseId)),
                          0
                        )
                      )}
                </div>
              </div>
              <div className='loading-spinner'></div>
            </div>
          </div>
        )}

        {showCompletionScreen && (
          <div style={{ position: 'relative' }}>
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle={false}
              numberOfPieces={200}
              colors={[
                '#74b9ff',
                '#0984e3',
                '#ff7675',
                '#e17055',
                '#fdcb6e',
                '#6c5ce7',
              ]}
              style={{ position: 'fixed', top: 0, left: 0, zIndex: 1 }}
            />
            <div style={{ position: 'relative', zIndex: 2 }}>
              <h1>You did it!</h1>
              <div className='completion-screen'>
                <p>
                  You completed the{' '}
                  {isSkillMode ? skillDisplayData.name : session.title}{' '}
                  {isSkillMode ? 'Skill' : 'Workout'}!
                </p>

                {/* Player Points Display */}
                <div className='player-points-display'>
                  {selectedPlayersData && selectedPlayersData.length > 0 ? (
                    <div className='players-grid'>
                      {selectedPlayersData.map((player, index) => (
                        <div key={player.id} className='player-point-card'>
                          <div className='player-avatar'>
                            {player.avatar || '🐶'}
                          </div>
                          <p className='player-name'>{player.playerName}</p>
                          <div style={{ fontSize: '12px' }}>Earned</div>
                          <div className='player-points'>
                            {isSkillMode
                              ? skillDrills.reduce(
                                  (sum, drill) => sum + drill.points,
                                  0
                                )
                              : session.completion.points}{' '}
                            Points
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='player-point-card'>
                      <div className='player-avatar'>🐶</div>
                      <div className='player-name'>Player</div>
                      <div style={{ fontSize: '12px' }}>Earned</div>
                      <div className='player-points'>
                        {isSkillMode
                          ? skillDrills.reduce(
                              (sum, drill) => sum + drill.points,
                              0
                            )
                          : session.completion.points}{' '}
                        Points
                      </div>
                    </div>
                  )}
                </div>

                <p>
                  Great job,{' '}
                  {sportConfigs[sport]?.terminology?.player || 'star'}!{' '}
                  {sportConfigs[sport]?.emoji || '⭐'}
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                  }}
                >
                  <button
                    onClick={handleBackToLeaderboard}
                    className='completion-btn button button-secondary'
                  >
                    View Leaderboard
                  </button>
                  <button
                    onClick={handleBackToWorkouts}
                    className='completion-btn button button-primary'
                  >
                    Back to {isSkillMode ? 'Skills' : 'Workouts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Exercise Screen */}
        {!showStartScreen &&
          !showIntroScreen &&
          !showCompletionScreen &&
          exercise && (
            <>
              {/* End Workout Button - Top Left */}
              <button
                className='end-workout-btn-secondary'
                onClick={handleEndWorkout}
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  background: '#333',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  zIndex: 10,
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#fff';
                  e.target.style.color = '#1a1a2e';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#fff';
                }}
              >
                End Workout
              </button>

              {/* Extract emoji and text from session title */}
              {isSkillMode && (
                <div
                  style={{
                    fontSize: '32px',
                    textAlign: 'center',
                    marginBottom: '5px',
                  }}
                >
                  {skillDisplayData.icon}
                </div>
              )}
              <h3 style={{ fontSize: '16px' }}>
                {isSkillMode ? skillDisplayData.name : session.title}{' '}
                {isSkillMode ? 'Skill' : 'Workout'}
              </h3>

              <div className='exercise-header'>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <p>
                    {currentExercise + 1} /{' '}
                    {isSkillMode
                      ? skillDrills.length
                      : session.exercises.length}
                  </p>
                  <p style={{ margin: '0' }}>
                    Earn{' '}
                    {isSkillMode
                      ? exercise.points
                      : getExercisePoints(
                          session.exercises[currentExerciseRef.current]
                        )}{' '}
                    Points
                  </p>
                </div>
                <h2>
                  {isSkillMode ? exercise.title : exercise.name}{' '}
                  {isSkillMode ? 'Drill' : 'Exercise'}
                </h2>
              </div>

              {/* Updated Countdown Timer */}
              <div className='countdown-timer'>
                <div
                  className='timer-display'
                  style={{
                    fontSize: '128px',
                    fontWeight: '700',
                    fontFamily: 'inherit',
                  }}
                >
                  {isSkillMode
                    ? formatTimeVerbose(
                        Math.max(totalExerciseTime - currentTime, 0)
                      )
                    : Math.max(totalExerciseTime - currentTime, 0)}
                </div>
                <div className='timer-progress'>
                  <div
                    className='timer-bar'
                    style={{
                      width: `${
                        totalExerciseTime > 0
                          ? (currentTime / totalExerciseTime) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Player Emojis Display */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '20px',
                  marginTop: '20px',
                  flexWrap: 'wrap',
                }}
              >
                {selectedPlayersData && selectedPlayersData.length > 0 ? (
                  selectedPlayersData.map((player, index) => (
                    <div
                      key={player.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        lineHeight: '1',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '144px',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                          lineHeight: '1',
                        }}
                      >
                        {player.avatar || '🐶'}
                      </div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#fff',
                          textAlign: 'center',
                        }}
                      >
                        {player.playerName}
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      lineHeight: '1',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '144px',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                        lineHeight: '1',
                      }}
                    >
                      🐶
                    </div>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#fff',
                        textAlign: 'center',
                      }}
                    >
                      Player
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

        {/* Loading Screen */}
        {!showStartScreen &&
          !showIntroScreen &&
          !showCompletionScreen &&
          !exercise && <h2>Loading...</h2>}
      </div>
    );
  } catch (error) {
    console.error('❌ Error in AudioWorkoutScreen:', error);
    return (
      <div>
        Error loading {isSkillMode ? 'skill' : 'workout'}. Please try again.
      </div>
    );
  }
}

export default AudioWorkoutScreen;
